import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Plus,
  Edit,
  Eye,
  Trash2,
  Download,
  AlertTriangle,
  Calendar,
  DollarSign,
  Users,
  Target,
  Clock,
  CheckCircle,
  BarChart3,
  MoreHorizontal,
  RefreshCw,
  FileText,
  Settings,
  Shield,
  TrendingUp,
  Activity,
  HelpCircle,
  ArrowUpRight,
  ArrowDownRight
} from 'lucide-react';
import PageBreadcrumb from '../common/PageBreadcrumb';
import PageHeader from '../common/PageHeader';
import DataTable from '../common/DataTable';
import ConfirmationModal from '../common/ConfirmationModal';
import ActionMenu from '../common/ActionMenu';
import HelpButton from '../common/HelpButton';
import { useToast, ToastContainer } from '../common/Toast';
import API_CONFIG from '../../config/api';
import './RiskManagement.css';

// Custom Dropdown Component
const ActionDropdown = ({ risk, onView, onEdit, onDelete }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [openUpward, setOpenUpward] = useState(false);
  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    const handleScroll = () => setIsOpen(false);

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      window.addEventListener('scroll', handleScroll, true);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [isOpen]);

  const handleToggle = () => {
    if (!isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setOpenUpward(window.innerHeight - rect.bottom < 160);
    }
    setIsOpen(v => !v);
  };
  const handleDelete = () => {
    setIsOpen(false);
    setTimeout(() => {
      onDelete?.(risk);
    }, 0);
  };

  return (
    <div ref={dropdownRef} className="relative" style={{ zIndex: isOpen ? 10000 : 'auto' }}>
      <button
        ref={buttonRef}
        type="button"
        onMouseDown={(e) => e.stopPropagation()}
        onClick={(e) => {
          e.stopPropagation();
          handleToggle();
        }}
        className="inline-flex items-center justify-center w-8 h-8 border border-gray-200 bg-white text-[var(--gold-primary)] rounded hover:bg-[#FFF9E6] hover:border-[var(--gold-primary)] transition-colors shadow-sm"
      >
        <MoreHorizontal size={16} />
      </button>

      {isOpen && (
        <div
          className="fixed min-w-[160px] bg-white border border-gray-200 rounded-lg shadow-lg"
          style={{
            zIndex: 10001,
            [openUpward ? 'bottom' : 'top']: openUpward
              ? `${window.innerHeight - buttonRef.current.getBoundingClientRect().top + 8}px`
              : `${buttonRef.current.getBoundingClientRect().bottom + 8}px`,
            left: `${buttonRef.current.getBoundingClientRect().left}px`,
            transform: 'translateX(-75%)',
          }}
        >
          <ul className="py-1 m-0 list-none">
            <li>
              <button
                onMouseDown={(e) => e.stopPropagation()}
                onClick={(e) => {
                  e.stopPropagation();
                  setIsOpen(false);
                  onView(risk);
                }}
                className="flex items-center w-full px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 text-left"
              >
                <Eye size={16} className="mr-2.5 text-gray-500" />
                View Risk
              </button>
            </li>
            <li>
              <button
                onMouseDown={(e) => e.stopPropagation()}
                onClick={(e) => {
                  e.stopPropagation();
                  setIsOpen(false);
                  onEdit(risk);
                }}
                className="flex items-center w-full px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 text-left"
              >
                <Edit size={16} className="mr-2.5 text-gray-500" />
                Edit Risk
              </button>
            </li>
            <li className="border-t border-gray-200">
              <button
                onMouseDown={(e) => e.stopPropagation()}
                onClick={(e) => {
                  e.stopPropagation();
                  handleDelete();
                }}
                className="flex items-center w-full px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 text-left"
              >
                <Trash2 size={16} className="mr-2.5 text-red-600" />
                Delete Risk
              </button>
            </li>
          </ul>
        </div>
      )}
    </div>
  );
};

const RiskManagement = () => {
  const navigate = useNavigate();
  const { success, error: showError, toasts, removeToast } = useToast();

  const [risks, setRisks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  const [isDeleting, setIsDeleting] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0
  });
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [riskToDelete, setRiskToDelete] = useState(null);
  const [stats, setStats] = useState({
    totalRisks: 0,
    highRisks: 0,
    mediumRisks: 0,
    lowRisks: 0
  });

  const calculateRiskScore = (impact, probability) => {
    const impactScores = { Low: 2, Medium: 3, High: 5 };
    const probabilityScores = { Low: 2, Medium: 3, High: 5 };

    return (impactScores[impact] || 1) * (probabilityScores[probability] || 1);
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      loadRisks();
    }, 400);
    return () => clearTimeout(timer);
  }, [pagination.page, pagination.limit, searchTerm]);

  useEffect(() => {
    // Debounce search
    const timer = setTimeout(() => {
      if (pagination.page === 1) {
        loadRisks();
      } else {
        setPagination(prev => ({ ...prev, page: 1 }));
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // 🔹 Compute stats directly from risk table data (single source of truth)
  const computeStatsFromRisks = (risksList) => {
    let high = 0;
    let medium = 0;
    let low = 0;

    risksList.forEach(risk => {
      const score = risk.riskScore || 0;
      if (score >= 15) high++;
      else if (score >= 7) medium++;
      else low++;
    });
    setStats({
      totalRisks: risksList.length,
      highRisks: high,
      mediumRisks: medium,
      lowRisks: low
    });
  };

  const loadRisks = async () => {
    setLoading(true);
    try {

      let url = `${API_CONFIG.ENDPOINTS.RISKS}?page=${pagination.page}&limit=${pagination.limit}`;
      if (searchTerm) {
        url += `&search=${encodeURIComponent(searchTerm)}`;
      }

      const response = await fetch(url, {
        headers: API_CONFIG.HEADERS
      });

      if (response.ok) {
        const data = await response.json();
        const resolveUserName = (user) => {
          if (!user) return null;
          if (typeof user === 'string') return user; // fallback
          if (user.firstName || user.lastName) {
            return `${user.firstName || ''} ${user.lastName || ''}`.trim();
          }
          return user.name || user.email || null;
        };
        const mappedRisks = (data.risks || []).map(risk => {
          return {
            id: risk._id || risk.id,
            _id: risk._id,
            name: risk.name || '',
            description: risk.description || '',
            category: risk.category || '',
            status: risk.status || 'Open',
            priority: risk.priority || 'Medium',
            impact: risk.impact || 'Medium',
            probability: risk.probability || 'Medium',
            riskScore: calculateRiskScore(risk.impact || 'Medium', risk.probability || 'Medium'),
            project: risk.project ? {
              name: risk.project.name || '',
              id: risk.project._id || risk.project.id,
              status: risk.project.status || ''
            } : null,
            owner: risk.owner
              ? {
                name: resolveUserName(risk.owner),
                id: risk.owner._id || risk.owner.id || risk.owner,
                email: risk.owner.email || ''
              }
              : risk.ownerName
                ? {
                  name: risk.ownerName,
                  id: null,
                  email: ''
                }
                : null,
            assignedTo: risk.assignedTo ? {
              name: (risk.assignedTo.firstName && risk.assignedTo.lastName
                ? `${risk.assignedTo.firstName} ${risk.assignedTo.lastName}`
                : risk.assignedTo.name || ''),
              id: risk.assignedTo._id || risk.assignedTo.id
            } : null,
            identifiedDate: risk.identifiedDate
              ? new Date(risk.identifiedDate).toISOString().split('T')[0]
              : '',
            targetDate: risk.targetDate
              ? new Date(risk.targetDate).toISOString().split('T')[0]
              : '',
            mitigationStrategy: risk.mitigationStrategy || ''
          };
        });
        setRisks(mappedRisks);
        computeStatsFromRisks(mappedRisks); // 🔹 stats now match table

        setPagination(prev => ({
          ...prev,
          total: data.total || 0
        }));

      } else {
        const errorData = await response.json().catch(() => ({}));
        showError(errorData.message || 'Failed to load risks');
        setRisks([]);
      }
    } catch (err) {
      console.error('Error loading risks:', err);
      showError(`Network error: ${err.message}`);
      setRisks([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (value) => {
    setSearchTerm(value);
  };

  const handleCreateRisk = () => {
    navigate('/projects/risks/create');
  };

  const handleViewRisk = (risk) => {
    const riskId = risk._id || risk.id;
    if (riskId) {
      navigate(`/projects/risks/view/${riskId}`);
    } else {
      showError('Invalid risk ID');
    }
  };

  const handleEditRisk = (risk) => {
    const riskId = risk._id || risk.id;
    if (riskId) {
      navigate(`/projects/risks/edit/${riskId}`);
    } else {
      showError('Invalid risk ID');
    }
  };

  const handleDeleteRisk = (risk) => {
    setRiskToDelete(risk);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!riskToDelete) return;

    setIsDeleting(true);

    try {
      const riskId = riskToDelete._id || riskToDelete.id;

      const response = await fetch(
        `${API_CONFIG.ENDPOINTS.RISKS}/${riskId}`,
        {
          method: 'DELETE',
          headers: API_CONFIG.HEADERS
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to delete risk');
      }

      success('Risk deleted successfully!');
      await loadRisks();

      setShowDeleteModal(false);
      setRiskToDelete(null);
    } catch (err) {
      console.error(err);
      showError(err.message);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleRefresh = () => {
    loadRisks();
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  // Handle help button click
  const handleHelpClick = () => {
    const helpUrl = '/help/risk-management';
    const helpWindow = window.open(
      helpUrl,
      'helpWindow',
      'width=1200,height=800,scrollbars=yes,resizable=yes,menubar=no,toolbar=no,location=no,status=no'
    );
    if (helpWindow) {
      helpWindow.focus();
    }
  };

  // Define action buttons for PageHeader
  const headerActions = [
    {
      label: '', // Empty label for icon-only square button
      icon: <HelpCircle size={18} />,
      onClick: handleHelpClick,
      variant: 'outline',
      tooltip: 'Help (Press ? for keyboard shortcut)'
    },
    {
      label: '', // Empty label for icon-only square button
      icon: <RefreshCw size={18} className={loading ? 'spinning' : ''} />,
      onClick: handleRefresh,
      variant: 'outline',
      tooltip: 'Refresh risks list',
      disabled: loading
    },
    {
      label: 'New Risk',
      icon: <Plus size={18} />,
      onClick: handleCreateRisk,
      variant: 'primary',
      tooltip: 'Create a new risk'
    }
  ];

  const getStatusBadge = (status) => {
    const statusConfig = {
      'Open': { color: '#dc2626', bg: '#fef2f2', text: 'Open' },
      'Mitigated': { color: '#ea580c', bg: '#fff7ed', text: 'Mitigated' },
      'Closed': { color: '#16a34a', bg: '#f0fdf4', text: 'Closed' },
      'Monitoring': { color: '#2563eb', bg: '#eff6ff', text: 'Monitoring' }
    };

    const config = statusConfig[status] || statusConfig['Open'];

    return (
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          padding: '4px 8px',
          borderRadius: '6px',
          fontSize: '12px',
          fontWeight: '500',
          backgroundColor: config.bg,
          color: config.color,
          border: `1px solid ${config.color}20`
        }}
      >
        {config.text}
      </span>
    );
  };

  const getPriorityBadge = (priority) => {
    const priorityConfig = {
      'Critical': { color: '#dc2626', bg: '#fef2f2' },
      'High': { color: '#ea580c', bg: '#fff7ed' },
      'Medium': { color: '#d97706', bg: '#fffbeb' },
      'Low': { color: '#16a34a', bg: '#f0fdf4' }
    };

    const config = priorityConfig[priority] || priorityConfig['Medium'];

    return (
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          padding: '4px 8px',
          borderRadius: '6px',
          fontSize: '12px',
          fontWeight: '500',
          backgroundColor: config.bg,
          color: config.color,
          border: `1px solid ${config.color}20`
        }}
      >
        {priority}
      </span>
    );
  };

  const getRiskScoreColor = (score) => {
    if (score >= 15) return '#dc2626';   // High
    if (score >= 10) return '#ea580c';   // Medium
    return '#16a34a';                  // Low
  };


  const columns = [
    {
      key: '__spacer__',
      label: '',
      width: '16px',
      sortable: false,
      render: () => <div style={{ width: '16px' }} />
    },
    {
      key: 'name',
      label: 'Risk Name',
      render: (value, risk) => {
        const riskData = risk || {};
        return (
          <div className="d-flex align-items-center">
            <div
              className="me-3"
              style={{
                width: '40px',
                height: '40px',
                borderRadius: '8px',
                backgroundColor: '#fef2f2',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: '1px solid var(--gold-primary)'
              }}
            >
              <AlertTriangle size={20} style={{ color: '#dc2626' }} />
            </div>
            <div>
              <div
                style={{
                  fontWeight: '600',
                  color: '#1f2937',
                  fontSize: '14px',
                  marginBottom: '2px'
                }}
              >
                {riskData.name || 'N/A'}
              </div>
            </div>
          </div>
        );
      }
    },
    {
      key: 'category',
      label: 'Category',
      render: (value, risk) => {
        const riskData = risk || {};
        return (
          <span style={{ fontWeight: '500', color: '#374151' }}>
            {riskData.category || 'N/A'}
          </span>
        );
      }
    },
    {
      key: 'status',
      label: 'Status',
      render: (value, risk) => {
        const riskData = risk || {};
        return getStatusBadge(riskData.status || 'Open');
      }
    },
    {
      key: 'priority',
      label: 'Priority',
      render: (value, risk) => {
        const riskData = risk || {};
        return getPriorityBadge(riskData.priority || 'Medium');
      }
    },
    {
      key: 'riskScore',
      label: 'Risk Score',
      render: (value, risk) => {
        const riskData = risk || {};
        const score = riskData.riskScore || 0;
        return (
          <div className="d-flex align-items-center">
            <div
              style={{
                width: '40px',
                height: '8px',
                backgroundColor: '#e5e7eb',
                borderRadius: '4px',
                marginRight: '8px',
                overflow: 'hidden'
              }}
            >
              <div
                style={{
                  width: `${(score / 25) * 100}%`,
                  height: '100%',
                  backgroundColor: getRiskScoreColor(score),
                  borderRadius: '4px',
                  transition: 'width 0.3s ease'
                }}
              />
            </div>
            <span
              style={{
                fontWeight: '600',
                color: getRiskScoreColor(score),
                fontSize: '14px'
              }}
            >
              {score}
            </span>
          </div>
        );
      }
    },
    {
      key: 'project',
      label: 'Project',
      render: (value, risk) => {
        const riskData = risk || {};
        const project = riskData.project || {};
        return (
          <div>
            <div style={{ fontWeight: '500', color: '#1f2937', fontSize: '14px' }}>
              {project.name || 'N/A'}
            </div>
            <div style={{ color: '#6b7280', fontSize: '12px' }}>
              {project.status || ''}
            </div>
          </div>
        );
      }
    },
    {
      key: 'owner',
      label: 'Owner',
      render: (value, risk) => {
        const riskData = risk || {};
        const owner = riskData.owner || {};
        const ownerName =
          owner?.name && owner.name.trim().length > 0
            ? owner.name
            : 'Unknown';
        return (
          <div className="d-flex align-items-center">
            <div
              style={{
                width: '32px',
                height: '32px',
                borderRadius: '50%',
                backgroundColor: '#007B8F',
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '12px',
                fontWeight: '600',
                marginRight: '8px'
              }}
            >
              {ownerName.charAt(0).toUpperCase()}
            </div>
            <div>
              <div style={{ fontWeight: '500', color: '#1f2937', fontSize: '14px' }}>
                {ownerName}
              </div>
              <span className="px-3 py-1 rounded-full text-xs font-bold uppercase" style={{ backgroundColor: '#FFF9E6', color: 'var(--gold-primary)' }}>
                Risk Owner
              </span>
            </div>
          </div>
        );
      }
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (value, risk) => {
        const riskData = risk || {};
        const riskId = riskData._id || riskData.id;
        if (!riskId) {
          console.error('Invalid risk data in render function:', riskData);
          return <span style={{ color: 'red' }}>Error</span>;
        }
        return (
          <ActionDropdown
            risk={riskData}
            onView={handleViewRisk}
            onEdit={handleEditRisk}
            onDelete={handleDeleteRisk}
          />
        );
      }
    }
  ];

  return (
    <>
      {/* Breadcrumb - Outside page-container to align with sidebar */}
      <PageBreadcrumb />
      {/* Page Header - Outside page-container to align with sidebar */}
      <PageHeader
        title="Risk Management"
        description="Identify, assess, and manage project risks"
        actions={headerActions}
      />
      <div className="page-container">

        {/* Stats Cards */}
        <div className="grid max-w-[1200px] mx-auto w-full grid-cols-[repeat(auto-fit,minmax(220px,1fr))] gap-4 mb-6">
          {[
            {
              icon: AlertTriangle,
              value: stats.totalRisks,
              label: 'Total Risks',
              trend: '+8%',
              up: true,
            },
            {
              icon: Shield,
              value: stats.highRisks,
              label: 'High Risks',
              trend: '-3%',
              up: false,
            },
            {
              icon: Activity,
              value: stats.mediumRisks,
              label: 'Medium Risks',
              trend: '+5%',
              up: true,
            },
            {
              icon: CheckCircle,
              value: stats.lowRisks,
              label: 'Low Risks',
              trend: '+2%',
              up: true,
            },].map(({ icon: Icon, value, label, trend, up }, i) => (
              <div
                key={i}
                className="relative cursor-pointer border border-[var(--border-color)] bg-white shadow-md transition-all hover:-translate-y-1 hover:border-[var(--gold-primary)] hover:shadow-[0_8px_25px_rgba(0,0,0,0.15)]"
              >
                {/* Accent line */}
                <div className="h-1 w-full bg-[var(--gold-primary)]" />
                <div className="relative flex items-center gap-3 p-4">
                  {/* Trend */}
                  <div
                    className={`absolute right-4 top-3 flex items-center gap-1 text-[12px] font-semibold ${up ? 'text-[#059669]' : 'text-[#dc2626]'
                      }`}
                  >
                    {up ? <ArrowUpRight size={14} /> : <ArrowDownRight size={14} />}
                    {trend}
                  </div>
                  {/* Icon */}
                  <div className="flex h-11 w-11 shrink-0 items-center justify-center bg-[#FFF9E6]">
                    <Icon size={20} className="text-[var(--gold-primary)]" />
                  </div>
                  {/* Text */}
                  <div>
                    <div className="text-[20px] font-bold text-[var(--text-primary)]">
                      {value}
                    </div>
                    <div className="mt-1 text-[13px] text-[var(--text-secondary)]">
                      {label}
                    </div>
                  </div>
                </div>
              </div>
            ))}
        </div>

        {/* Risks Table */}
        <DataTable
          data={risks}
          columns={columns}
          title="Risks"
          loading={loading}
          pagination={pagination}
          onPageChange={handlePageChange}
          searchable={true}
          emptyMessage="No risks found. Create your first risk to get started."
          emptyIcon={<AlertTriangle size={48} />}
          showActions={false}
          showCheckboxes={false}
        />

        {/* Modals */}
        <ConfirmationModal
          isOpen={showDeleteModal}
          onClose={() => setShowDeleteModal(false)}
          onConfirm={confirmDelete}
          title="Delete Risk"
          message={`Are you sure you want to delete "${riskToDelete?.name}"? This action cannot be undone.`}
          confirmText="Delete"
          cancelText="Cancel"
          type="danger"
          isLoading={isDeleting}
        />
        <ToastContainer toasts={toasts} removeToast={removeToast} />
      </div>
    </>
  );
};

export default RiskManagement;
